import { Link } from 'react-router-dom';
import { CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';

import { useDuongDan } from '../../../../hooks/useDuongDan';
import { useT } from '../../../../i18n';
import type { KhoaDich } from '../../../../i18n';
import { cn } from '../../../../lib/cn';
import { useNguonVet } from '../../su-dung-ai/nguon';
import { soNguyen } from '../nghiep-vu/so-thong-ke';

interface MucChatLuong {
  khoa: string;
  cap: 'canh' | 'nang';
  cau: string;
  lienKet?: { den: string; nhan: KhoaDich };
}

/**
 * Dải nhắc về CHẤT LƯỢNG của chính những con số đang hiện bên dưới — port `thong-ke/DauChatLuong.tsx` native (Tổng
 * quan và tab Tổng quan của Sử dụng AI cùng dùng).
 *
 * Chỉ cổng nền tảng: tenant không thấy giá vốn, token thật (ADR-0123 mục 1, 5).
 *
 * Nó ở TRÊN, không phải chú thích chân trang: `so_chua_co_gia`, `so_uoc_tinh`, `so_co_canh_bao` nói rằng giá vốn và
 * token thật ngay dưới ĐANG KHÔNG ĐỦ hoặc KHÔNG ĐÁNG TIN. Người ta chụp màn hình con số tổng rồi dán vào báo cáo — một
 * chú thích dưới biểu đồ không có trong ảnh chụp ấy.
 */
export function DauChatLuong({
  soChuaCoGia,
  soThieuTyGia = 0,
  soUocTinh,
  soCoCanhBao,
  soLuot,
}: {
  soChuaCoGia: number;
  /** Phần của `soChuaCoGia` do thiếu tỷ giá — việc phải làm khác hẳn khai giá. */
  soThieuTyGia?: number;
  /** Chỉ có ở `tong`; chỗ không đo được (dòng phân rã) truyền 0. */
  soUocTinh: number;
  soCoCanhBao: number;
  /** 0 lượt thì không có gì để nói về chất lượng — dải nhắc tự ẩn. */
  soLuot: number;
}) {
  const { t, locale } = useT();
  const duongDan = useDuongDan();
  const { lienKetBangGia } = useNguonVet();

  if (soLuot === 0) return null;

  const muc: MucChatLuong[] = [];
  const soThieuDongGia = soChuaCoGia - soThieuTyGia;
  if (soThieuDongGia > 0) {
    muc.push({
      khoa: 'chuaCoGia',
      cap: 'nang',
      cau: t('nktv.chatLuong.chuaCoGia', { so: soNguyen(soThieuDongGia, locale) }),
      lienKet: lienKetBangGia ? { den: lienKetBangGia, nhan: 'nktv.chatLuong.moBangGia' } : undefined,
    });
  }
  if (soThieuTyGia > 0) {
    muc.push({
      khoa: 'thieuTyGia',
      cap: 'nang',
      cau: t('nktv.chatLuong.thieuTyGia', { so: soNguyen(soThieuTyGia, locale) }),
      lienKet: lienKetBangGia ? { den: lienKetBangGia, nhan: 'nktv.chatLuong.moBangGia' } : undefined,
    });
  }
  if (soCoCanhBao > 0) {
    muc.push({ khoa: 'coCanhBao', cap: 'nang', cau: t('nktv.chatLuong.coCanhBao', { so: soNguyen(soCoCanhBao, locale) }) });
  }
  if (soUocTinh > 0) {
    muc.push({ khoa: 'uocTinh', cap: 'canh', cau: t('nktv.chatLuong.uocTinh', { so: soNguyen(soUocTinh, locale) }) });
  }

  if (muc.length === 0) {
    return (
      <p className="flex items-start gap-2 rounded-lg bg-success-50 px-4 py-2.5 text-xs leading-relaxed text-success-700">
        <CheckCircleOutlined aria-hidden className="mt-0.5 shrink-0" />
        <span>{t('nktv.chatLuong.daDu')}</span>
      </p>
    );
  }

  return (
    <section
      aria-label={t('nktv.chatLuong.tieuDe')}
      className="rounded-lg border border-default bg-surface px-5 py-4 shadow-sm"
    >
      <h2 className="flex items-center gap-2 text-sm font-semibold text-content-primary">
        <WarningOutlined aria-hidden className="text-warning-600" />
        {t('nktv.chatLuong.tieuDe')}
      </h2>
      <ul className="mt-2 space-y-1.5">
        {muc.map((m) => (
          <li
            key={m.khoa}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-xs leading-relaxed text-content-secondary"
          >
            <span
              aria-hidden
              className={cn(
                'h-1.5 w-1.5 shrink-0 translate-y-[-1px] rounded-full',
                m.cap === 'nang' ? 'bg-danger-500' : 'bg-warning-500',
              )}
            />
            <span className="min-w-0 flex-1">{m.cau}</span>
            {m.lienKet && (
              <Link
                to={duongDan(m.lienKet.den)}
                className="shrink-0 rounded font-medium text-content-link underline decoration-dotted underline-offset-2 hover:text-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
              >
                {t(m.lienKet.nhan)}
              </Link>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
